<template>
  <div class="medication-class-dosage-interface">
    <medication-text-field
        :is-editable="isEditable"
        v-model="computedDosageValue"
        :placeholder="minMaxPlaceholder"
        is-special-placeholder
        :error="(+dosageValue <= 0 || Number.isNaN(Number(dosageValue))) && showErrors"
        class="medication__dosage"
        type="Number"
        @onKeypress="onKeypress"
        @onPaste="onPaste"
        :min="0"
        :auto-select-all="false"
    ></medication-text-field>
    <medication-recommendation-tooltip
        v-if="minMaxTooltip !== '' && !hideRecommendationInfo"
        :icon="dosageValueOutOfRange ? 'mdi-alert-circle' : null"
        :color="dosageValueOutOfRange ? 'grey' : null"
        :value="minMaxTooltip"
        class="mr-2 pt-2"
    />
    <span v-if="minMaxTooltip === '' || hideRecommendationInfo" class="tooltipPlaceHolder mr-2"></span>
    <medication-dosage-unit
        :is-editable="isEditable"
        :administration="administration"
        v-model="computedDosageUnit"
        class="mr-6"
        :recommended-units="minMaxDosageUnits"
        :hideRecommendationInfo="hideRecommendationInfo"
        :recommended-dosage-unit="recommendedDosageUnit"
    ></medication-dosage-unit>
    <medication-class
        v-if="hasMedicationClass"
        :is-editable="isEditable"
        :placeholder="$ucfirst($t('medication.medicationClass'))"
        :error="!medicationClass && showErrors"
        :recommended-classes="recommendedClasses"
        :selected-classes="selectedClasses"
        :is-recommendation="isRecommendation"
        v-model="computedMedicationClass"
        :hideRecommendationInfo="hideRecommendationInfo"
    ></medication-class>
    <v-tooltip
        v-if="isEditable && isRemovable"
        bottom
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
            class="ml-3"
            fab
            text
            small
            color="error"
            v-on="on"
            v-bind="attrs"
            @click="remove"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <span>{{ $t('medication.removeClass') }}</span>
    </v-tooltip>
    <v-text-field v-if="needsORSuffix" class="v-text-field__suffix"
                  :disabled="true"
                  hide-details
                  :placeholder="$t('expertTool.combination.or').toUpperCase()"
                  outlined
                  dense
    />
  </div>
</template>

<script>
import MedicationTextField from './MedicationTextField';
import MedicationDosageUnit from './MedicationDosageUnit';
import MedicationClass from './MedicationClass';
import MedicationRecommendationTooltip from "./MedicationRecommendationTooltip";
import {uniqueValues} from "../../../../../helper/arrays";

/**
 * Component MedicationClassDosageInterface.vue
 *
 * @module MedicationClassDosageInterface
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/components/Parts/Medication/MedicationClassDosageInterface.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'MedicationClassDosageInterface',
  components: {
    MedicationRecommendationTooltip,
    MedicationTextField,
    MedicationDosageUnit,
    MedicationClass,
  },
  props: {
    showErrors: {
      type: Boolean,
      default: () => false,
    },
    isRemovable: {
      type: Boolean,
      required: true,
    },
    isEditable: {
      type: Boolean,
      required: true,
    },
    medicationClass: {
      type: String,
      required: true,
    },
    dosageValue: {
      type: [Number, String],
      required: true,
    },
    dosageUnit: {
      type: String,
      required: true,
    },
    dosageId: {
      type: [String],
      default: null,
      required: false,
    },
    administration: {
      type: String,
      required: true
    },
    recommendedClasses: {
      type: Array,
      default: () => [],
    },
    selectedClasses: {
      type: Array,
      default: () => [],
    },
    isRecommendation: {
      type: Boolean,
      default: () => false
    },
    hasMedicationClass: {
      type: Boolean,
      default: () => true
    },
    needsORSuffix: {
      type: Boolean,
      default: () => false
    },
    hideRecommendationInfo: {
      type: Boolean,
      default: () => false
    }
  },
  computed: {
    dosageValueOutOfRange() {
      if (!(this.minDosageValues?.length || this.maxDosageValues?.length)) {
        return false;
      }

      if (Number.isNaN(Number(this.dosageValue))) {
        return true;
      }

      if (this.minDosageValues?.length && this.dosageValue < Math.min(...this.minDosageValues)) {
        return true;
      }

      if (this.maxDosageValues?.length && this.dosageValue > Math.max(...this.maxDosageValues)) {
        return true;
      }

      return false;
    },
    recommendedDosageUnit() {
      if (!this.minMaxDosageUnits?.length) {
        return null;
      }

      const uniqueUnits = new Set(this.minMaxDosageUnits);
      const only1KindOfUnit = uniqueUnits.size === 1;

      if (!only1KindOfUnit) {
        return null;
      }

      return this.minMaxDosageUnits[0];
    },
    maxDosageUnits() {
      return this.recommendedClasses.reduce((accumulator, item) => {
        if (item.key === this.medicationClass) {
          return [...accumulator, item.maxDosageUnit]
        }
        return accumulator;
      }, [])
    },
    minDosageUnits() {
      return this.recommendedClasses.reduce((accumulator, item) => {
        if (item.key === this.medicationClass) {
          return [...accumulator, item.minDosageUnit]
        }
        return accumulator;
      }, [])
    },
    minDosageValues() {
      return this.recommendedClasses.reduce((accumulator, item) => {
        if (item.key === this.medicationClass) {
          return [...accumulator, item.minDosageValue]
        }
        return accumulator;
      }, [])
    },
    maxDosageValues() {
      return this.recommendedClasses.reduce((accumulator, item) => {
        if (item.key === this.medicationClass) {
          return [...accumulator, item.maxDosageValue]
        }
        return accumulator;
      }, [])
    },
    computedDosageUnit: {
      get() {
        return this.dosageUnit;
      },
      set(dosageUnit) {
        this.$emit('update:dosageUnit', dosageUnit);
      },
    },
    computedDosageValue: {
      get() {
        return this.dosageValue;
      },
      set(dosageValue) {
        this.$emit('update:dosageValue', dosageValue.replace(',', '.'));
      },
    },
    computedMedicationClass: {
      get() {
        return this.medicationClass;
      },
      set(medicationClass) {
        this.$emit('update:medicationClass', medicationClass);
      },
    },
    minMaxValuesUnits: {
      get() {
        // Zip it
        if (this.minDosageValues.length) {
          return this.minDosageValues
              .reduce((accumulator, item, i) => {
                // Only handle ranges with same unit
                if (this.isMinMaxDosageUnitValidToShow(this.minDosageUnits?.[i], this.maxDosageUnits?.[i])) {
                  return [...accumulator, {
                    minDosageValue: item,
                    minDosageUnit: this.minDosageUnits?.[i],
                    maxDosageValue: this.maxDosageValues?.[i],
                    maxDosageUnit: this.maxDosageUnits?.[i]
                  }];
                }

                return accumulator;
              }, []);
        } else if (this.maxDosageValues.length) {
          return this.maxDosageValues
              .reduce((accumulator, item, i) => {
                // Only handle ranges with same unit
                if (this.isMinMaxDosageUnitValidToShow(this.minDosageUnits?.[i], this.maxDosageUnits?.[i])) {
                  return [...accumulator, {
                    minDosageValue: this.minDosageValues?.[i],
                    minDosageUnit: this.minDosageUnits?.[i],
                    maxDosageValue: item,
                    maxDosageUnit: this.maxDosageUnits?.[i]
                  }];
                }

                return accumulator;
              }, []);
        }

        return [];
      }
    },
    minMaxPlaceholder: {
      get() {
        let minMaxText = '';

        const uniqueMinMaxValuesUnits = this.getUniqueMinMaxValuesUnits(this.minMaxValuesUnits);
        // Only when all ranges are the same show placeholder
        if (uniqueMinMaxValuesUnits.length === 1) {
          minMaxText = this.getMinMaxText(uniqueMinMaxValuesUnits[0]);
        }

        return minMaxText;
      }
    },
    minMaxTooltip: {
      get() {
        let minMaxText = '';

        // Don't show the same label
        const uniqueMinMaxValuesUnits = this.getUniqueMinMaxValuesUnits(this.minMaxValuesUnits);

        uniqueMinMaxValuesUnits.forEach((minMaxValUnit, i, arr) => {
          const minMaxRowText = this.getMinMaxText(minMaxValUnit, true);

          if (minMaxRowText !== '') {
            minMaxText += minMaxRowText;
            // Check if not last
            if (arr.length > 1 && i !== arr.length - 1) {
              // Add break
              minMaxText += '</br>';
            }
          }
        });

        return minMaxText;
      }
    },
    minMaxDosageUnits: {
      get() {
        return this.minMaxValuesUnits.reduce((accumulator, item) => {
          return uniqueValues([...accumulator, item?.minDosageUnit, item?.maxDosageUnit])
        }, []);
      }
    }
  },
  methods: {
    remove() {
      this.$emit('remove');
    },
    getMinMaxText(minMaxValueUnit, showUnits = false) {
      let minMaxText = '';
      const hasMin = minMaxValueUnit?.minDosageValue !== null && minMaxValueUnit?.minDosageValue !== undefined;
      const hasMax = minMaxValueUnit?.maxDosageValue !== null && minMaxValueUnit?.maxDosageValue !== undefined;
      const minIsMax = minMaxValueUnit?.minDosageValue === minMaxValueUnit?.maxDosageValue;

      if (hasMin && !hasMax) {
        minMaxText += this.$t('min', {catalog: 'app_dashboard'}).toUpperCase() + ' ';
      }

      if (hasMin) {
        minMaxText += minMaxValueUnit.minDosageValue;
        if (showUnits) {
          minMaxText += ' ' + minMaxValueUnit.minDosageUnit;
        }
      } else if (hasMax) {
        minMaxText += this.$t('max', {catalog: 'app_dashboard'}).toUpperCase() + ' ';
      }

      if (hasMin && hasMax && !minIsMax) {
        if (showUnits) {
          minMaxText += ' - ';
        } else {
          minMaxText += '-';
        }
      }

      if (hasMax && !hasMin || (hasMin && hasMax && !minIsMax)) {
        minMaxText += minMaxValueUnit.maxDosageValue;
        if (showUnits) {
          minMaxText += ' ' + minMaxValueUnit.maxDosageUnit;
        }
      }

      return minMaxText;
    },
    getUniqueMinMaxValuesUnits(arr) {
      if (arr.length) {
        return arr.filter((value, index, self) =>
                index === self.findIndex((t) => (
                    t?.minDosageValue === value?.minDosageValue && t?.minDosageUnit === value?.minDosageUnit &&
                    t?.maxDosageValue === value?.maxDosageValue && t?.maxDosageUnit === value?.maxDosageUnit &&
                    ((t?.minDosageValue !== null && t?.minDosageValue !== undefined &&
                            t?.minDosageUnit !== null && t?.minDosageUnit !== undefined) ||
                        (t?.maxDosageValue !== null && t?.maxDosageValue !== undefined &&
                            t?.maxDosageUnit !== null && t?.maxDosageUnit !== undefined))
                ))
        );
      }

      return [];
    },
    isMinMaxDosageUnitValidToShow(minDosageUnit, maxDosageUnit) {
      const hasMin = minDosageUnit !== null && minDosageUnit !== undefined;
      const hasMax = maxDosageUnit !== null && maxDosageUnit !== undefined;
      const minIsMax = minDosageUnit === maxDosageUnit;

      return (hasMin && hasMax && minIsMax) || hasMin && !hasMax || !hasMin && hasMax;
    },
    onKeypress(event) {
      const onlyNumbers = /[\d,.]/
      if (!onlyNumbers.test(event.key)) {
        event.preventDefault();
      }
    },
    onPaste(event) {
      const pasted = event.clipboardData.getData('text/plain')
      const parsed = parseFloat(pasted)
      event.preventDefault()

      if (!isNaN(parsed) && !(parsed < 0)) {
        const replaced = pasted.replaceAll(',','.')
        this.$emit('update:dosageValue', parseFloat(replaced));
      } else {
        this.$emit('update:dosageValue', '');
      }
    }
  },
};
</script>

<style lang="scss">
.medication-class-dosage-interface {
  display: flex;

  .tooltipPlaceHolder {
    width: 24px;
    height: 24px;
  }
  .v-text-field__suffix {
    margin-left: 8px;
    width: 70px;
    position: relative;
    top: 28px;
    border: 1px solid #f7cc85;
    background: rgba(0, 0, 0, 0.54);


    &:before, &:after {
      content: " ";
      position: absolute;
      width: 16px;
      height: 1px;
      background: #f7cc85;
      border-radius: 2px;
      pointer-events: none;
    }

    &:before {
      transform: translate(-14px, -3px) rotate(29deg);
    }

    &:after {
      transform: translate(-14px, 3px) rotate(-29deg);
      bottom: 0;
    }

    input {
      text-align: center;
    }
  }
}

.medication-class-dosage-interface ~ .medication-class-dosage-interface {
  margin-top: 16px;
}
</style>
